import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import MenuIcon from "@material-ui/icons/Menu";
import Navbar from "components/Navbar";
import CustomersNavbar from "components/Masterbar/CustomersBar";
import DarkLayout from "components/Shared/DarkLayout";
import ZipcodesVisualizationMap from "components/Zipcodes/Visualization/ZipcodesVisualizationMap";
import ZipcodesVisualizationSidebar from "components/Zipcodes/Visualization/ZipcodesVisualizationSidebar";
import ZipcodesVisualizationDetail from "components/Zipcodes/Visualization/ZipcodesVisualizationDetail";
import { PATHS } from "util/appConstants";
import {
  getZipcodes,
  selectZipcodeStatus,
  selectZipcodes,
  selectZipcodesTimestamp,
} from "redux/slices/zipcodeSlice";
import { getTours, selectTours } from "redux/slices/tourSlice";
import { getSuppliers, selectSuppliers } from "redux/slices/supplierSlice";
import { selectUser } from "redux/slices/userSlice";
import { Box, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import {
  allVisualizationSupplierIds,
  buildMapPointsForTours,
  buildVisualizationSupplierOptions,
  buildZipcodeVisualizationByTour,
  countMappableZipcodesForTours,
  filterTourGroupsBySupplier,
  isDeliveryTour,
  loadVisualizationTourSelection,
  mapPointToDetailSelection,
  saveVisualizationTourSelection,
  zipMatchesSupplierFilter,
} from "util/zipcodeVisualization";

const SIDEBAR_EXPANDED = 380;
const SIDEBAR_COLLAPSED = 60;
const MAP_LAYOUT_TOP = 128;

export default function ZipcodesVisualization() {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const user = useSelector(selectUser);
  const loading = useSelector(selectZipcodeStatus);
  const zipcodes = useSelector(selectZipcodes);
  const timestamp = useSelector(selectZipcodesTimestamp);
  const tours = useSelector(selectTours);
  const suppliers = useSelector(selectSuppliers);

  const canView = Boolean(user?.permissions?.zipcodes?.visualization);
  const seeAllSuppliers = Boolean(user?.permissions?.zipcodes?.see_all_suppliers);

  useEffect(() => {
    if (user && !canView) {
      history.push("/");
    }
  }, [user, canView, history]);

  const [selectedTourIds, setSelectedTourIds] = useState(() => loadVisualizationTourSelection());
  const [selectedSupplierIds, setSelectedSupplierIds] = useState([]);
  const [selectedDepartureTimes, setSelectedDepartureTimes] = useState([]);
  const [suppliersFilterReady, setSuppliersFilterReady] = useState(false);
  const suppliersFilterInitializedRef = useRef(false);
  const [detail, setDetail] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const canEdit = Boolean(user?.permissions?.zipcodes?.create);

  const deliveryTours = useMemo(
    () => (tours || []).filter(isDeliveryTour),
    [tours],
  );

  const tourGroups = useMemo(
    () => buildZipcodeVisualizationByTour(zipcodes, deliveryTours),
    [zipcodes, deliveryTours],
  );

  const supplierOptions = useMemo(
    () => buildVisualizationSupplierOptions(tourGroups, suppliers),
    [tourGroups, suppliers],
  );

  const allSupplierIds = useMemo(
    () => allVisualizationSupplierIds(supplierOptions),
    [supplierOptions],
  );

  const activeSupplierIds = useMemo(() => {
    if (!suppliersFilterReady) {
      return allSupplierIds;
    }
    return selectedSupplierIds;
  }, [suppliersFilterReady, allSupplierIds, selectedSupplierIds]);

  const departureOptions = useMemo(() => {
    const times = new Set();
    for (const tour of tours || []) {
      for (const config of tour.delivery_timing_configs || []) {
        if (config.delivery_timing) {
          times.add(config.delivery_timing);
        }
      }
    }
    return [...times].sort().map((time) => ({ id: time, name: time }));
  }, [tours]);

  const filteredTourGroups = useMemo(() => {
    // 1. Build the full tour groups with nested departures
    const fullGroups = tourGroups.map((group) => {
      const tour = tours.find((t) => Number(t.id) === group.tourId);
      const configs = tour?.delivery_timing_configs || [];

      // Map zipcodes with their departure strings
      const zipcodesWithDep = group.zipcodes.map((zip) => {
        const finalDeps = (zip.departures && zip.departures.length > 0)
          ? zip.departures
          : (zip.departure ? [zip.departure] : configs.map((c) => c.delivery_timing).filter(Boolean));
        return {
          ...zip,
          departures: finalDeps,
        };
      });

      // Group zipcodes by departure config
      const departures = configs.map((config) => {
        const zips = zipcodesWithDep.filter((z) =>
          z.departures.includes(config.delivery_timing),
        );
        return {
          id: config.id,
          delivery_timing: config.delivery_timing,
          supplier_location_id: config.supplier_location_id,
          zipcodes: zips,
        };
      });

      return {
        ...group,
        zipcodes: zipcodesWithDep,
        departures,
      };
    });

    // 2. Filter by selectedSupplierIds (using activeSupplierIds)
    let temp = fullGroups.map((group) => ({
      ...group,
      zipcodes: group.zipcodes.filter((zip) =>
        zipMatchesSupplierFilter(zip, activeSupplierIds),
      ),
      departures: group.departures.map((dep) => ({
        ...dep,
        zipcodes: dep.zipcodes.filter((zip) =>
          zipMatchesSupplierFilter(zip, activeSupplierIds),
        ),
      })),
    }));

    // 3. Filter by selectedDepartureTimes
    if (selectedDepartureTimes.length > 0) {
      temp = temp
        .map((group) => {
          const filteredDeps = group.departures.filter((dep) =>
            selectedDepartureTimes.includes(dep.delivery_timing),
          );
          // Keep zipcodes belonging to any of the matching departures
          const filteredZips = group.zipcodes.filter((zip) =>
            zip.departures.some((d) => selectedDepartureTimes.includes(d)),
          );
          return {
            ...group,
            departures: filteredDeps,
            zipcodes: filteredZips,
          };
        })
        .filter((group) => group.departures.length > 0);
    }

    return temp;
  }, [tourGroups, tours, activeSupplierIds, selectedDepartureTimes]);

  const mapPoints = useMemo(
    () => buildMapPointsForTours(filteredTourGroups, selectedTourIds),
    [filteredTourGroups, selectedTourIds],
  );

  const mapSelectionKey = useMemo(
    () =>
      `${selectedTourIds.slice().sort((a, b) => a - b).join(",")}:${selectedSupplierIds
        .slice()
        .sort((a, b) => a - b)
        .join(",") || "none"}`,
    [selectedTourIds, selectedSupplierIds],
  );

  const mappableStats = useMemo(
    () => countMappableZipcodesForTours(filteredTourGroups, selectedTourIds),
    [filteredTourGroups, selectedTourIds],
  );

  const showNoGeolocationHint =
    selectedTourIds.length > 0 && mappableStats.mappable === 0 && mappableStats.total > 0;

  useEffect(() => {
    if (!loading && !timestamp) {
      dispatch(getZipcodes());
    }
  }, [dispatch, loading, timestamp]);

  useEffect(() => {
    dispatch(getTours());
    dispatch(getSuppliers());
  }, [dispatch]);

  useEffect(() => {
    saveVisualizationTourSelection(selectedTourIds);
  }, [selectedTourIds]);

  useEffect(() => {
    if (!allSupplierIds.length) {
      return;
    }
    if (!suppliersFilterInitializedRef.current) {
      suppliersFilterInitializedRef.current = true;
      setSelectedSupplierIds([...allSupplierIds]);
      setSuppliersFilterReady(true);
      return;
    }
    setSelectedSupplierIds((prev) =>
      prev.filter((id) => allSupplierIds.includes(Number(id))),
    );
  }, [allSupplierIds]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev || "";
    };
  }, []);

  const handleTourSelectionChange = (ids) => {
    setSelectedTourIds(ids);
    setDetail(null);
    setHoveredId(null);
  };

  const handleSupplierFilterChange = (supplierIds) => {
    setSelectedSupplierIds(supplierIds);
    setDetail(null);
    setHoveredId(null);
  };

  const handleZipcodeClick = (zip) => {
    setDetail(zip);
  };

  const handleMapSelect = (point) => {
    setDetail(mapPointToDetailSelection(point));
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const selectedZipcode = params.get("selected_zipcode");
    if (!selectedZipcode) {
      return;
    }
    if (!tourGroups.length) {
      return;
    }
    if (!suppliersFilterReady) {
      return;
    }
    if (detail) {
      return;
    }

    const selectedCountry = params.get("country");
    const selectedTourIdRaw = params.get("tour_id");
    const selectedTourId = selectedTourIdRaw != null ? Number(selectedTourIdRaw) : null;
    const normalizedZip = String(selectedZipcode).trim();
    const normalizedCountry =
      selectedCountry != null ? String(selectedCountry).trim().toUpperCase() : null;

    let match = null;
    for (const tour of tourGroups) {
      for (const zip of tour.zipcodes) {
        if (String(zip.zipcode ?? "").trim() !== normalizedZip) {
          continue;
        }
        if (normalizedCountry && String(zip.country ?? "").toUpperCase() !== normalizedCountry) {
          continue;
        }
        if (Number.isFinite(selectedTourId) && Number(zip.tourId) !== selectedTourId) {
          continue;
        }
        match = zip;
        break;
      }
      if (match) {
        break;
      }
    }

    if (match) {
      setDetail(match);
      if (Number.isFinite(selectedTourId) && !selectedTourIds.includes(selectedTourId)) {
        setSelectedTourIds((prev) => [...new Set([...prev, selectedTourId])]);
      }
    }

    history.replace(PATHS.zipcodes.visualization);
  }, [
    location.search,
    tourGroups,
    suppliersFilterReady,
    detail,
    selectedTourIds,
    history,
  ]);

  const handleEdit = () => {
    if (!detail) {
      return;
    }
    const path = PATHS.zipcodes.edit.replace(
      ":zipcode",
      encodeURIComponent(detail.zipcode),
    );
    const params = new URLSearchParams();
    params.set("source", "visualization");
    if (detail.country) {
      params.set("country", detail.country);
    }
    if (detail.tourId != null) {
      params.set("tour_id", String(detail.tourId));
    }
    const search = params.toString() ? `?${params.toString()}` : "";
    history.push(`${path}${search}`);
  };

  const selectedId = detail?.id ?? null;

  if (user && !canView) {
    return null;
  }

  return (
    <>
      <Navbar />
      <CustomersNavbar />
      <DarkLayout
        pl={2}
        pr={1}
        nopadding
        fullHeight
        loading={loading}
        pt={0}
        pb={0}
        style={{
          position: "fixed",
          top: MAP_LAYOUT_TOP,
          left: 0,
          right: 0,
          bottom: 0,
          height: "auto",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <div style={{ position: "relative", width: "100%", height: "100%", minHeight: 0 }}>
          <div style={{ position: "absolute", inset: 0 }}>
            <ZipcodesVisualizationMap
              points={mapPoints}
              selectionKey={mapSelectionKey}
              hoveredId={hoveredId}
              selectedId={selectedId}
              onHover={setHoveredId}
              onSelect={handleMapSelect}
            />
            {showNoGeolocationHint && (
              <Box
                style={{
                  position: "absolute",
                  top: 16,
                  left: sidebarCollapsed ? SIDEBAR_COLLAPSED + 16 : SIDEBAR_EXPANDED + 16,
                  right: 16,
                  zIndex: 5,
                  background: "rgba(18, 18, 18, 0.92)",
                  color: "#fff",
                  padding: "12px 16px",
                  borderRadius: 8,
                  maxWidth: 420,
                  transition: "left 0.3s ease",
                }}
              >
                <Typography variant="body2">
                  {t("ZIPCODE_VISUALIZATION_NO_GEO", {
                    total: mappableStats.total,
                  })}
                </Typography>
              </Box>
            )}
          </div>
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              bottom: 0,
              width: sidebarCollapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED,
              background: "#121212",
              zIndex: 10,
              transition: "width 0.3s ease",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                width: SIDEBAR_EXPANDED,
                height: "100%",
                overflowY: sidebarCollapsed ? "hidden" : "auto",
                overflowX: "hidden",
                opacity: sidebarCollapsed ? 0 : 1,
                visibility: sidebarCollapsed ? "hidden" : "visible",
                transition: "opacity 0.15s ease",
                WebkitOverflowScrolling: "touch",
              }}
            >
              {detail ? (
                <ZipcodesVisualizationDetail
                  selection={detail}
                  canEdit={canEdit}
                  seeAllSuppliers={seeAllSuppliers}
                  onBack={() => setDetail(null)}
                  onEdit={handleEdit}
                  onToggleSidebar={() => setSidebarCollapsed(true)}
                />
              ) : (
                <ZipcodesVisualizationSidebar
                  tourGroups={filteredTourGroups}
                  selectedTourIds={selectedTourIds}
                  seeAllSuppliers={seeAllSuppliers}
                  supplierOptions={supplierOptions}
                  selectedSupplierIds={selectedSupplierIds}
                  suppliersFilterReady={suppliersFilterReady}
                  onSupplierFilterChange={handleSupplierFilterChange}
                  departureOptions={departureOptions}
                  selectedDepartureTimes={selectedDepartureTimes}
                  onDepartureFilterChange={setSelectedDepartureTimes}
                  onTourSelectionChange={handleTourSelectionChange}
                  onZipcodeClick={handleZipcodeClick}
                  onZipcodeHover={setHoveredId}
                  hoveredId={hoveredId}
                  mapPoints={mapPoints}
                  onToggleSidebar={() => setSidebarCollapsed(true)}
                />
              )}
            </div>
          </div>
          {sidebarCollapsed && (
            <div
              style={{
                position: "absolute",
                top: 17,
                left: 7,
                zIndex: 11,
                background: "#121212",
                borderRadius: 8,
                padding: 6,
                cursor: "pointer",
              }}
              onClick={() => setSidebarCollapsed(false)}
            >
              <MenuIcon style={{ color: "white" }} />
            </div>
          )}
        </div>
      </DarkLayout>
    </>
  );
}
